import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { HandDisplay } from '../components/problems/HandDisplay';
import { AuctionTable } from '../components/problems/AuctionTable';
import { VulnerabilityIndicator } from '../components/problems/VulnerabilityIndicator';
import { PlayTable } from '../components/counting/PlayTable';
import { BidText } from '../components/common/BidToken';
import { formatBidToken } from '../utils/formatBidToken';
import { AskJeffCommentEditor } from '../components/ask-jeff/AskJeffCommentEditor';
import { askJeffApi } from '../api/askJeffApi';
import type { AskJeffReviewItem, AskJeffSection } from '../types/askJeff';

const DIRECTION_NAMES: Record<'N' | 'E' | 'S' | 'W', string> = {
  N: 'North',
  E: 'East',
  S: 'South',
  W: 'West',
};

/**
 * "Ask Jeff — Review" (feature 025) — the Super Editor's surface for the hands he flagged while
 * practising. Each hand is shown the way he met it, except that the auction is complete and the
 * answer is stated: this is a review, not a quiz. Underneath sit his own notes and the box where
 * Jeff's verdict is recorded.
 *
 * <p>
 * Layout follows the "3rd level bids - Review" page (feature 017) — the same gray panel, the
 * seat-relative hand/vulnerability grouping (`.vuln-hand-group[data-seat]`, feature 016), and the
 * same Previous/Next action row — so the two review surfaces read as one family. Counting hands
 * are the exception: they are a play problem, so they render through `PlayTable`.
 * </p>
 *
 * <p>
 * Route access is Super Editor-only (App.tsx), and every endpoint it calls enforces the same —
 * Admin included.
 * </p>
 */
export function AskJeffReviewPage() {
  usePageTitle('Ask Jeff - Review — Hawkes Bridge');

  const navigate = useNavigate();
  const [section, setSection] = useState('all');
  const [sections, setSections] = useState<AskJeffSection[]>([]);
  const [items, setItems] = useState<AskJeffReviewItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const pageScrollRef = useRef<HTMLDivElement>(null);

  // Only the newest load may commit. Changing the section while the previous list is still in
  // flight starts a second request without cancelling the first, and the two can resolve in
  // either order — when the older "all sections" response landed last it overwrote the filtered
  // one, leaving the page showing hands from every section while the dropdown named just one.
  // A monotonic sequence number makes the last request asked for the one that wins, whichever
  // response arrives last.
  const loadSeq = useRef(0);

  const load = useCallback((next: string) => {
    const seq = ++loadSeq.current;
    setLoading(true);
    setError(null);
    Promise.all([askJeffApi.getReview(next), askJeffApi.getSections()])
      .then(([reviewItems, sectionList]) => {
        if (seq !== loadSeq.current) return;
        setItems(reviewItems);
        setSections(sectionList);
        setCurrentIndex(0);
      })
      .catch(() => {
        if (seq !== loadSeq.current) return;
        setError('Unable to load the flagged hands. Please try again.');
      })
      .finally(() => {
        if (seq !== loadSeq.current) return;
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    load(section);
  }, [section, load]);

  const total = items.length;
  const current = total > 0 ? items[currentIndex] : null;

  useEffect(() => {
    window.scrollTo?.(0, 0);
    pageScrollRef.current?.scrollTo?.(0, 0);
  }, [current?.tableResultId]);

  const atFirst = currentIndex <= 0;
  const atLast = currentIndex >= total - 1;

  // Both writes update the item in place rather than reloading the list: a reload would reset the
  // position to the first hand, which is the wrong place to be after saving the fifth.
  const handleSaveComment = useCallback(async (comment: string | null) => {
    if (!current) return;
    await askJeffApi.saveComment(current.tableResultId, comment);
    setItems(prev => prev.map(i =>
      i.tableResultId === current.tableResultId ? { ...i, jeffComment: comment } : i));
  }, [current]);

  const handleSaveNote = useCallback(async (note: string | null) => {
    if (!current) return;
    await askJeffApi.updateNote(current.tableResultId, note);
    setItems(prev => prev.map(i =>
      i.tableResultId === current.tableResultId ? { ...i, note } : i));
  }, [current]);

  return (
    <div ref={pageScrollRef} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col">
      {/* Header: title + position indicator + Exit */}
      <div className="flex items-center gap-4 short:gap-2 mb-3 short:mb-2">
        <h1 className="text-lg sm:text-xl font-bold text-ink">Ask Jeff - Review</h1>
        <div className="ml-auto flex items-center gap-4 short:gap-2">
          {!loading && !error && total > 0 && (
            <div className="flex items-center bg-cream border border-line rounded-lg px-3 py-1.5">
              <span className="text-sm text-ink-soft" data-testid="ask-jeff-position">
                Hand: {currentIndex + 1} / {total}
              </span>
            </div>
          )}
          <button
            type="button"
            onClick={() => navigate('/data-editing')}
            className="px-3 py-1.5 text-sm font-medium text-ink-soft hover:text-ink border border-line-strong rounded-md"
          >
            Exit
          </button>
        </div>
      </div>

      {/* Section filter + Refresh */}
      <div className="flex items-center flex-wrap gap-2 mb-4 short:mb-2">
        <label htmlFor="ask-jeff-section" className="text-sm font-bold text-ink">
          Section:
        </label>
        <select
          id="ask-jeff-section"
          value={section}
          onChange={e => setSection(e.target.value)}
          disabled={loading}
          className="border border-line-strong rounded-md px-2 py-1 text-sm"
        >
          <option value="all">All sections</option>
          {sections.map(s => (
            <option key={s.sectionKey} value={s.sectionKey}>
              {s.sectionLabel} ({s.count})
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => load(section)}
          disabled={loading}
          data-testid="ask-jeff-refresh"
          className="px-3 py-1 text-sm font-medium rounded-md bg-cream text-ink border border-brass hover:bg-paper disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Refresh
        </button>
      </div>

      {loading && (
        <div className="flex items-center justify-center min-h-[40vh]">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {!loading && error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700" role="alert">
          {error}
        </div>
      )}

      {!loading && !error && total === 0 && (
        <div className="bg-cream border border-line rounded-lg p-6 text-ink-soft" data-testid="ask-jeff-empty">
          No hands have been flagged for Jeff{section === 'all' ? ' yet' : ' in this section'}. Use the
          “Ask Jeff” button while practising to add one.
        </div>
      )}

      {!loading && !error && current && (
        <>
          {/* Metadata bar — the hand id, the section it was flagged under, and the scoring. */}
          <div className="mb-4 short:mb-3">
            <div
              className="bg-card border border-line rounded-lg p-2 short:p-1.5 flex flex-wrap gap-4 short:gap-3 text-sm short:text-xs"
              data-testid="ask-jeff-meta"
            >
              <span>
                <span className="text-ink-soft">Number (ID):</span>{' '}
                <span className="font-semibold text-ink">{current.tableResultId}</span>
              </span>
              <span>
                <span className="text-ink-soft">Section:</span>{' '}
                <span className="font-semibold text-ink">{current.sectionLabel}</span>
              </span>
              {current.hand && (
                <span className="text-red-600 font-bold">
                  <span>Scoring:</span> <span>{current.hand.scoringType || 'N/A'}</span>
                </span>
              )}
              {current.hand?.tournamentTitle && (
                <span className="hidden md:inline">
                  <span className="text-ink-soft">From:</span>{' '}
                  <span className="font-medium text-ink">{current.hand.tournamentTitle}</span>
                </span>
              )}
            </div>
          </div>

          {/* The question, with its answer revealed. */}
          {current.handUnavailable && (
            <div
              className="bg-cream border border-line rounded-lg p-4 mb-3 text-ink-soft"
              data-testid="ask-jeff-hand-unavailable"
            >
              This hand can no longer be shown — it may have been excluded or re-curated out of its
              section since it was flagged. Your notes and Jeff's comment below still apply to hand
              #{current.tableResultId}.
            </div>
          )}

          {current.renderer === 'counting' && current.countingProblem && (
            <div className="bg-cream rounded-lg p-4 short:p-3 mb-3" data-testid="ask-jeff-counting">
              <PlayTable problem={current.countingProblem} />
              <div className="mt-3 bg-card border border-line rounded-lg p-3">
                <div className="text-base text-ink">
                  <span className="font-medium">Question:</span>{' '}
                  {current.countingProblem.questionText}
                </div>
                <hr className="my-2 border-line" />
                <div className="text-base text-ink" data-testid="ask-jeff-answer">
                  <span className="font-medium">Answer:</span>{' '}
                  <span className="font-semibold">{current.countingProblem.correctAnswer}</span>
                </div>
              </div>
            </div>
          )}

          {current.renderer === 'auction' && current.hand && (
            <div className="bg-cream rounded-lg p-4 short:p-3 narrow:p-2 mb-3 short:mb-1 mobile-flex-col">
              <div className="flex flex-col flex-1 min-h-0 narrow:justify-start narrow:items-center lphone-outer">
                <div className="flex flex-wrap flex-1 min-h-0 gap-3 short:gap-2 narrow:gap-1.5 justify-center items-stretch narrow:flex-col narrow:flex-nowrap narrow:items-center narrow:justify-start lphone-items problem-items">
                  {/* Vulnerability board + You Hold — placement follows the hand's seat. */}
                  <div
                    className="flex gap-3 short:gap-2 narrow:gap-1.5 items-center flex-shrink-0 vuln-hand-group"
                    data-seat={current.hand.decisionSeat}
                  >
                    <div className="flex-shrink-0">
                      <VulnerabilityIndicator
                        vulnerability={current.hand.vulnerability}
                        vulnerabilityText={current.hand.vulnerability}
                        decisionSeat={current.hand.decisionSeat}
                        dealer={(current.hand.dealer || undefined) as 'N' | 'E' | 'S' | 'W' | undefined}
                      />
                    </div>
                    <div className="bg-card border border-line rounded-lg p-2 short:p-1.5 flex-shrink-0 w-[clamp(9rem,40vw,11rem)] md:w-[clamp(7rem,16.5vw,9rem)] lg:w-[clamp(8rem,18vw,12rem)] lphone-youhold">
                      <h2 className="font-semibold text-ink short:h-[1.75rem] short:text-[0.95rem]" style={{ fontSize: '1rem' }}>
                        You hold{' '}
                        <span className="text-ink-soft font-normal">
                          (<span className="lg:hidden">{current.hand.decisionSeat}</span>
                          <span className="hidden lg:inline">{DIRECTION_NAMES[current.hand.decisionSeat]}</span>)
                        </span>
                      </h2>
                      <HandDisplay hand={current.hand.playerHand} />
                    </div>
                  </div>

                  {/* The COMPLETE auction — nothing masked. */}
                  <div className="bg-card border border-line rounded-lg p-2 short:p-1.5 flex flex-col auction-container-width">
                    <h2 className="font-semibold text-ink mb-1 short:mb-0.5 auction-header" style={{ fontSize: '1rem' }}>
                      The Auction
                    </h2>
                    <div className="auction-scroll scrollable flex-1">
                      <AuctionTable
                        auction={current.hand.auction}
                        vulnerability={current.hand.vulnerability}
                        decisionSeat={current.hand.decisionSeat}
                      />
                    </div>
                  </div>

                  {/* The question and its revealed answer. */}
                  <div className="feedback-container-width self-stretch narrow:flex-auto narrow:self-auto">
                    <div
                      className="border border-line bg-card rounded-lg p-3 short:p-2 self-start narrow:self-auto"
                      data-testid="ask-jeff-answer"
                    >
                      <div className="text-base short:text-sm text-ink">
                        <span className="font-medium">Question:</span>{' '}
                        <BidText text={current.hand.prompt} />
                      </div>
                      <hr className="my-2 short:my-1.5 border-line" />
                      {/* Yes/No drills name Jeff's own action as a self-contained phrase
                          ("Jeff passed" / "Jeff opened 1♥") with no "Answer:" label, exactly as
                          the practice page does (FR-017d) — the raw no:pass / no:other token is
                          a grading form, not something to read. Call modules keep the label. */}
                      <div className="text-base short:text-sm text-ink" data-testid="ask-jeff-answer-text">
                        {current.hand.actualCall ? (
                          <span className="font-semibold">
                            {/* Same pass test the practice page uses — formatBidToken maps the
                                raw tournament token 'p' to 'Pass'. */}
                            {formatBidToken(current.hand.actualCall) === 'Pass' ? (
                              'Jeff passed'
                            ) : (
                              <>
                                Jeff opened <BidText text={formatBidToken(current.hand.actualCall)} />
                              </>
                            )}
                          </span>
                        ) : (
                          <>
                            <span className="font-medium">Answer:</span>{' '}
                            <span className="font-semibold">
                              <BidText text={formatBidToken(current.hand.answer)} />
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Notes + Jeff's comment. */}
          <div className="mb-3">
            <AskJeffCommentEditor
              tableResultId={current.tableResultId}
              comment={current.jeffComment}
              note={current.note}
              sectionLabel={current.sectionLabel}
              onSave={handleSaveComment}
              onSaveNote={handleSaveNote}
            />
          </div>

          {/* Previous / Next */}
          <div className="flex flex-wrap gap-3 short:gap-2 mt-auto flex-shrink-0">
            <button
              type="button"
              onClick={() => !atFirst && setCurrentIndex(i => i - 1)}
              disabled={atFirst}
              data-testid="ask-jeff-previous"
              className="px-5 py-2.5 short:px-3 short:py-1 short:text-sm rounded-md font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:bg-paper disabled:text-ink-soft disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => !atLast && setCurrentIndex(i => i + 1)}
              disabled={atLast}
              data-testid="ask-jeff-next"
              className="px-5 py-2.5 short:px-3 short:py-1 short:text-sm rounded-md font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:bg-paper disabled:text-ink-soft disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}
